
  import { Component, Emit, Vue } from 'vue-property-decorator'

  import TixMessage from '@/components/elements/TixMessage.vue'
  import TixTemplate from '@/components/elements/TixTemplate.vue'
  import FormInput2 from '@/components/forms/FormInput2.vue'
  import { isValidationError } from '@/errors/ValidationError'
  import { getApiErrorEntity } from '@/errors/helpers'
  import { makeOTPField } from '@/helpers/Authentication'
  import EventBus from '@/helpers/EventBus'
  import { loginWithOTP } from '@/helpers/OTP'
  import { reportFormValidity } from '@/helpers/Validation'
  import type { LanguageStrings } from '@/language/types'
  import type { LoginFlowData } from '@/store/LoginFlow'

  type LoginFlowDataOTPStep = LoginFlowData & { token: string }

  @Component({
    name: 'LoginFlowStepOTP',
    components: { FormInput2, TixTemplate, TixMessage },
  })
  export default class extends Vue {
    @Emit('goBack')
    goBack() {}

    t: LanguageStrings['loginFlowStepOTP']
    otp: string = ' '.repeat(6)
    submitting: boolean = false
    error: string | null = null
    retryCount: number = 0
    mustRequestNewToken: boolean = false
    codePossiblyHasntArrived: boolean = false
    codePossiblyHasntArrivedTimer: number | undefined

    created() {
      const isFromMagicLink = this.$route.query.otp

      if (isFromMagicLink) {
        this.otp = this.$route.query.otp

        // remove query param from url
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { otp, ...query } = this.$route.query
        this.$router.replace({ query })
        delete window.tixAppState?.queryParams?.otp

        this.submit()
      }

      // If the user takes more than 30 seconds to enter the code, we show a message to ask them to check their email.
      this.codePossiblyHasntArrivedTimer = setTimeout(() => {
        this.codePossiblyHasntArrived = true
        this.codePossiblyHasntArrivedTimer = undefined
      }, 30 * 1000)
    }

    destroyed() {
      this.resetCodePossiblyHasntArrived()
    }

    get field() {
      return makeOTPField()
    }

    get MAX_RETRIES(): number {
      return 3
    }

    get isDisabled() {
      return this.submitting || this.mustRequestNewToken
    }

    get loginFlowData(): LoginFlowDataOTPStep {
      return this.$store.getters['LoginFlow/all']
    }

    resetCodePossiblyHasntArrived() {
      this.codePossiblyHasntArrived = false

      if (this.codePossiblyHasntArrivedTimer !== undefined) {
        clearTimeout(this.codePossiblyHasntArrivedTimer)
        this.codePossiblyHasntArrivedTimer = undefined
      }
    }

    async submit() {
      this.submitting = true
      let autoRetrying = false
      this.resetCodePossiblyHasntArrived()

      try {
        await reportFormValidity(this.$el as HTMLFormElement)

        await loginWithOTP({ token: this.loginFlowData.token!, otp: this.otp })

        EventBus.$emit('tix:login:success')

        if (this.loginFlowData.redirectPath) {
          this.$router.replace(this.loginFlowData.redirectPath)
        }
      } catch (e: unknown) {
        if (isValidationError(e)) {
          this.error ??= this.t.invalidCode
          return
        }

        const apiError = getApiErrorEntity(e)
        if (!apiError) throw e

        let tokenExpired = false

        switch (apiError._code) {
          case 'not_available_yet':
            // The api rate limits the login request for 1 every 5 seconds. If we receive this error code, we keep the loading UI and retry the request after 5 seconds.
            setTimeout(() => this.submit(), 5000)
            autoRetrying = true
            break
          case 'otp_incorrect': {
            this.retryCount++
            const remainingAttempts = this.MAX_RETRIES - this.retryCount

            if (remainingAttempts <= 0) {
              tokenExpired = true
            } else {
              this.error = `${this.t.invalidCode} ${this.$t('loginFlowStepOTP.remainingAttempts', {
                count: remainingAttempts,
                s: remainingAttempts > 1 ? 's' : '',
              })}`
            }

            break
          }
          // NOTE: the api also returns `not_authorized` immediately after the token is requested, but before the email is sent. Technically this doesn't mean the token is expired, but we treat it as such to avoid confusion. This is an accepted behaviour for now: See https://tixtrackteam.slack.com/archives/CP9C2ET5E/p1746421295509899
          case 'not_authorized':
          case 'otp_token_expired':
          default:
            tokenExpired = true
            break
        }

        if (tokenExpired) {
          this.error = this.t.invalidCode
          this.mustRequestNewToken = true
        }
      } finally {
        if (!autoRetrying) {
          this.$nextTick(() => (this.submitting = false))
        }
      }
    }

    handleComplete() {
      this.submit()
    }

    handleChange(value: string) {
      this.otp = value
      this.error = null
    }
  }
